import { useEffect, useRef, useState } from "react";
import { 
    checkLiveTrackForUpdatesAPI, 
    getLiveTrackTableData, 
    launchPigAPI, 
    updatePasstimeAndSpeed 
} from "../services/liveTrakAPI";
import { useError } from "../contexts/ErrorContext";
import { useLoading } from "../contexts/LoadingContext";
import { useUser } from "@contexts/UserContext";
import estimatePassTimes from "../utils/liveTrackUtils";
import { toLocalISOString } from "../utils/dateAndTimeUtils";

// Custom hook for making API calls with error handling
const useApi = (apiFunc, loadingMessage, errorMessage) => {
    const { showError } = useError();
    const { showLoading, hideLoading } = useLoading();

    const callApi = async (...args) => {
        try {
            showLoading(loadingMessage);
            const response = await apiFunc(...args);
            hideLoading();

            // Handle our new structured error format
            if (response?.errorType) {
                showError(errorMessage || response.message || "An error occurred");
                return null;
            }

            return response;
        } catch (error) {
            // Fallback for any unexpected thrown errors
            hideLoading();
            showError(errorMessage || error.message || "An error occurred");
            return null;
        }
    };

    return callApi;
};

/**
 * LiveTrack, LiveTrackEdit, and MapComponent use this to get live data from an active run
 * 
 * @returns {Object[]} rawLiveTrackTable 
 * @returns {Object[]} formattedLiveTrackTable 
 * @returns {Function} fetchLiveTrackData 
 * @returns {Function} clearLiveTrackStates
 * @returns {Function} checkLiveTrackForUpdates
 * @returns {Function} findAndStoreLastUpdateValue
 * @returns {Function} handleSaveData
 * @returns {Object[]} runData
 * @returns {Function} setRunData
 * @returns {Boolean} noData
 * @returns {number} runMessageCount
 * @returns {Boolean} notificationsPaused
 * @returns {Function} setNotificationsPaused
 * 
 * @category Hooks
 */
const useLiveTrackData = () => {
    const { showError } = useError();
    const { showLoading, hideLoading } = useLoading();

    const [rawLiveTrackTable, setRawLiveTrackTable] = useState([]);
    const [formattedLiveTrackTable, setFormattedLiveTrackTable] = useState([]);
    const [runData, setRunData] = useState(null);

    const [noData, setNoData] = useState(false);

    const [runMessageCount, setRunMessageCount] = useState()
    const [notificationsPaused, setNotificationsPaused] = useState(false)

    const lastUpdateValue = useRef(null);
    const rawLiveTrackTableRef = useRef(rawLiveTrackTable);
    const consecutiveFailuresRef = useRef(0); //show error message if 3 network errors in a row (don't show just for one-off)
    const {updatePendingSpeedApprovals} = useUser()

    useEffect(() => {
        rawLiveTrackTableRef.current = rawLiveTrackTable;
    }, [rawLiveTrackTable]);

    const clearLiveTrackStates = () => {
        setFormattedLiveTrackTable([]);
        setRawLiveTrackTable([]);
    };

    const fetchLiveTrackData = useApi(
        getLiveTrackTableData, 
        'Fetching Live Track Data...', 
        'Error fetching live track data'
    );

    const fetchAndSetLiveTrackData = async (runID) => {
        showLoading('Loading Data...')
        const response = await fetchLiveTrackData(runID);

        if (response && response.success) {
            if (response.data.length === 0) {
                hideLoading();
                showError('No Live Track Data available for run');
                setNoData(true);
            } else {
                // Create new object references to ensure React detects changes after estimatePassTimes mutates
                const clonedData = response.data.map(item => ({ ...item }));

                //sort the items by Real Chain BEFORE estimatePassTimes
                //estimatePassTimes requires data to be in order by Real_Chain
                //Guard: handle missing AGMs or Real_Chain by treating them as Infinity (sorted to end)
                clonedData.sort((a, b) => {
                    const aChain = a.AGMs?.[0]?.Real_Chain;
                    const bChain = b.AGMs?.[0]?.Real_Chain;
                    const aVal = (aChain !== null && aChain !== undefined && !isNaN(aChain)) ? aChain : Infinity;
                    const bVal = (bChain !== null && bChain !== undefined && !isNaN(bChain)) ? bChain : Infinity;
                    return aVal - bVal;
                });

                setRawLiveTrackTable(clonedData);
                let dataWithEstPassTimes = estimatePassTimes(clonedData);

                setFormattedLiveTrackTable([...dataWithEstPassTimes]);
                findAndStoreLastUpdateValue();
                hideLoading();
                
            }
        } else {
            clearLiveTrackStates();
            hideLoading();
            const errorMsg = response?.message || 'Unknown error';
            showError(`There was a problem fetching live track data: ${errorMsg}`);
        }
    };

    const mergeUpdatesWithLTTable = (liveTrackUpdates) => {
        const liveTrackMap = new Map(liveTrackUpdates.map(item => [item.AGMID, item]));

        const updatedLTArr = rawLiveTrackTableRef.current.map(item => {
            const trackInfo = liveTrackMap.get(item.AGMID);
            return trackInfo ? { ...item, ...trackInfo } : item;
        });

        //sort by Real Chain BEFORE estimatePassTimes
        //Guard: handle missing AGMs or Real_Chain by treating them as Infinity (sorted to end)
        updatedLTArr.sort((a, b) => {
            const aChain = a.AGMs?.[0]?.Real_Chain;
            const bChain = b.AGMs?.[0]?.Real_Chain;
            const aVal = (aChain !== null && aChain !== undefined && !isNaN(aChain)) ? aChain : Infinity;
            const bVal = (bChain !== null && bChain !== undefined && !isNaN(bChain)) ? bChain : Infinity;
            return aVal - bVal;
        });

        setRawLiveTrackTable(updatedLTArr);
        let dataWithEstPassTimes = estimatePassTimes(updatedLTArr);

        setFormattedLiveTrackTable(dataWithEstPassTimes);
    };

    const checkLiveTrackForUpdates = async (runID) => {

        //same code as in useLiveSiteMap version of this function
        //set to present date/time if null
        //used to return if lastUpdateValue.current was null, which meant that updates weren't coming in if the pig had recently launched (so no est pass times available for markers)
        if (!lastUpdateValue.current) {
            lastUpdateValue.current = new Date()
        }
        
        const response = await checkLiveTrackForUpdatesAPI(lastUpdateValue.current, runID);

        if (response?.errorType) {
            // Increment consecutive failure count
            consecutiveFailuresRef.current += 1;
            
            // Only show error if we've had 3+ consecutive failures
            if (consecutiveFailuresRef.current >= 3) {
                // Enhanced error messages for better debugging infrequent network issues
                let errorMsg = response.message;
                if (response.errorType === 'NETWORK_ERROR') {
                    errorMsg += ' (This can happen if the server is temporarily unavailable or your connection drops)';
                } else if (response.errorType === 'HTTP_ERROR') {
                    errorMsg += ` (Status: ${response.status})`;
                }
                showError(`Error Fetching Live Track Updates - ${errorMsg}`);
            }
        } 
        else if (!response?.data) {
            // Increment consecutive failure count
            consecutiveFailuresRef.current += 1;
            
            // Only show error if we've had 3+ consecutive failures
            if (consecutiveFailuresRef.current >= 3) {
                showError("Error Fetching Live Track Updates - No data returned");
            }
        }
        else {
            // Success - reset failure counter
            consecutiveFailuresRef.current = 0;
            
            if (!response.data.liveTrackUpdates?.length) {
                //if liveTrackUpdates is an empty array, the run is not active, so there is no run data to update
                setRunData(null)
            } else {
            lastUpdateValue.current = response.data.lastUpdateTime ?? lastUpdateValue.current;
            mergeUpdatesWithLTTable(response.data.liveTrackUpdates);
            setRunData((prevData) => ({
                ...prevData,
                HasLaunched: 
                response.data?.liveTrackUpdates?.[0]?.Run != null
                    ? response.data.liveTrackUpdates[0].Run.HasLaunched
                    : prevData.HasLaunched,
            }));

            setNotificationsPaused(response.data?.liveTrackUpdates?.[0]?.Run?.IsStuck)
            setRunMessageCount(response.data?.runMessageCount)

            const hasPending = response.data?.hasPendingSpeedApprovals
            const speedApproverUserIDs = response.data?.speedApproverUserIDs
            
            updatePendingSpeedApprovals(runID, hasPending, speedApproverUserIDs)
            }
        }
    };

    const findAndStoreLastUpdateValue = () => {
        lastUpdateValue.current = rawLiveTrackTableRef.current.reduce(
            (latest, row) => row.UpdatedAt && (!latest || row.UpdatedAt > latest) ? row.UpdatedAt : latest, 
            null
        );
    };

    const handleSaveData = async(dateAndTime, initialSpeed, initialLaunch, selectedRow, setInitialLaunch, runData, setRunData, userID) => {
        const updatedData = rawLiveTrackTableRef.current.map(row => {
            if (row.ALTID === selectedRow.ALTID) {
                return {
                    ...row,
                    PassTime: dateAndTime ? dateAndTime : null,
                    Speed: initialLaunch ? initialSpeed : row.Speed,
                    Approved: true,
                    UserID: userID
                };
            }
            return row;
        });

        const dataWithEstPassTimes = estimatePassTimes(updatedData);

        const agmUpdateItem = dataWithEstPassTimes.find(agm => selectedRow.ALTID === agm.ALTID);

        showLoading('Verifying Pass Time');
        // if(!initialLaunch){
            const response = await updatePasstimeAndSpeed(agmUpdateItem.ALTID, agmUpdateItem, initialLaunch);

            if (response?.errorType || !response.success) {
                hideLoading();
                showError(`Error updating AGM data: ${response.message}`);
                return;
            }
    
            if (response.approval && response.approval.needApproval) {
                hideLoading();
                showError(`The Current Speed: ${response.approval.Curr_Speed}km/h is outside the boundaries for this run Low Speed: ${response.approval.Low_Speed}km/h & High Speed: ${response.approval.High_Speed}km/h - Approval from the supervisor has been requested`);
            } else {
                hideLoading();
                const newData = dataWithEstPassTimes.map(row => row.ALTID === response.data.ALTID ? {...row, Approved: response.data.Approved} : row);
                setFormattedLiveTrackTable(estimatePassTimes(newData));
                rawLiveTrackTableRef.current = updatedData;
            }
        // }
        

        

        if (initialLaunch) {
            setInitialLaunch(false);
            const updatedRunData = { ...runData, HasLaunched: true };
            setRunData(updatedRunData);
            const launchResponse = await launchPigAPI(runData.RunID);

            if (launchResponse?.errorType || !launchResponse.success) {
                const errorMessage = launchResponse.message ?? 'Error launching PIG'

                showError(errorMessage);
            }
            lastUpdateValue.current = toLocalISOString(new Date());
        }
    };

    return { 
        rawLiveTrackTable, 
        formattedLiveTrackTable, 
        fetchLiveTrackData: fetchAndSetLiveTrackData, 
        clearLiveTrackStates,
        checkLiveTrackForUpdates,
        findAndStoreLastUpdateValue,
        handleSaveData,
        runData,
        setRunData,
        noData,
        runMessageCount,
        notificationsPaused,
        setNotificationsPaused
    };
};

export default useLiveTrackData;
